import { createAsyncThunk } from '@reduxjs/toolkit';

import type { BaseAnalyticsData } from '@/pages/AppAnalytics/AppAnalytics';
import { AppAnalyticsSnowflakeService, SnowflakeQueries } from '@/pages/AppAnalytics/AppAnalyticsSnowflakeService';
import { FuelEntityType, FuelPortalService } from '@/services';
import type { Channel, Item } from '@/models';
import { dateUtils } from '@/utils/dateUtils';
import clientUtils from '@/utils/clientUtils';

import type { ExportAnalyticsJob } from './ExportAnalyticsJob';
import { removeJob, updateJob } from './ExportAnalyticsState';

const BATCH_SIZE = 100;
const DATE_FORMAT = 'MMM/dd/yyyy';

type ClipAnalyticsRow = {
  views: number;
  watchTime: number;
  averageWatchTime: number;
  title?: string;
};

export const processExportContentAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportContentAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.content, params);
    const total = data.length;
    const map: Record<string, ClipAnalyticsRow> = {};
    const clipIds = [];
    for (const row of data) {
      const key = row.video;
      if (key) {
        clipIds.push(key);
        map[key] = {
          views: row.count,
          watchTime: row.duration,
          averageWatchTime: row.average,
          title: row.title,
        };
      }
    }

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    // populate all clips data
    updateMessage(`Populating clip data`);
    let csv =
      'Title, ID, Type, Series, Season, Duration (seconds), Views, Watch Time (seconds), Average Watch Time (seconds), From (UTC), To (UTC)\n';
    let batch = [];
    for await (const clipId of clipIds) {
      if (batch.length === BATCH_SIZE) {
        const update = await processBatch(batch, map, range);
        csv += update;
        batch = [];

        const progress = (BATCH_SIZE / total) * 60 + 20;
        job.progress = progress;
        dispatch(updateJob(job));
      }
      batch.push(clipId);
    }
    const update = await processBatch(batch, map, range);
    csv += update;
    job.progress = 80;
    dispatch(updateJob(job));

    // export data as csv
    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `content-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

export const processExportLiveChannelsAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportLiveChannelsAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    // Match LiveChannelsList: watch + liveChannelWatch via SnowflakeQueries.channel
    const data = await service.getData(SnowflakeQueries.channel, params);

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    updateMessage(`Populating channel data`);

    // Snowflake `channel` is usually an alias. Match LiveChannelsList: load OU channels and index by id + alias.
    const channelByKey = new Map<string, Channel>();
    const ouChannels = await loadChannelsForOUs(params.selectedOUIds);
    indexChannels(channelByKey, ouChannels);
    indexChannels(channelByKey, params.selectedChannels || []);

    const guidIds = [
      ...new Set(
        data
          .map((row) => row.channel)
          .filter((id): id is string => !!id && clientUtils.isGuid(id) && !channelByKey.has(id))
      ),
    ];
    for (let i = 0; i < guidIds.length; i += BATCH_SIZE) {
      const batchIds = guidIds.slice(i, i + BATCH_SIZE);
      const channels = (await FuelPortalService.batchGet<Channel[]>(FuelEntityType.Channel, batchIds)) || [];
      indexChannels(channelByKey, channels);
    }

    let csv = 'Channel,Name,Views,Watch Time (seconds),Average Watch Time (seconds),From (UTC),To (UTC)\n';

    const progressRange = 60;
    data.forEach((row, i) => {
      const channelKey = row.channel ?? '';
      const portalChannel = channelByKey.get(channelKey);
      const channelAliasOrId = portalChannel?.alias || portalChannel?.id || channelKey || 'Unknown Channel';
      const channelName = portalChannel?.name ?? '';

      csv += `${csvCell(channelAliasOrId)},`;
      csv += `${csvCell(channelName)},`;
      csv += `${row.count},`;
      csv += `${row.duration},`;
      csv += `${row.average},`;
      csv += `${range.from},${range.to}\n`;

      job.progress = ((i + 1) / data.length) * progressRange + 20;
      dispatch(updateJob(job));
    });

    // export data as csv
    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `live-channels-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

export const processExportWatchAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportWatchAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.videoViews, params) as BaseAnalyticsData[];

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    // Build a lookup from group ID → display name using the resolved groupByList
    const groupLabelMap = new Map<string, string>(
      (params.groupByList || []).map((g) => [g.id, g.alias || g.name])
    );

    const groupByLabel =
      params.groupBy === 'channel' ? 'Channel'
      : params.groupBy === 'video' ? 'Video'
      : params.groupBy === 'category' ? 'Category'
      : 'Site';

    let csv = `Date,${groupByLabel},Views,Watch Time (seconds),Average Watch Time (seconds),From (UTC),To (UTC)\n`;

    const progressRange = 60;
    data.forEach((row, i) => {
      const date = (row.eventdate as string)?.substring(0, 10) ?? '';
      const groupId = String(row.site ?? '');
      const displayName = groupLabelMap.get(groupId) || groupId;

      csv += `${date},${displayName},${row.total ?? 0},${row.duration ?? 0},${row.average ?? 0},${range.from},${range.to}\n`;

      job.progress = ((i + 1) / data.length) * progressRange + 20;
      dispatch(updateJob(job));
    });

    job.progress = 80;
    dispatch(updateJob(job));

    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `watch-analytics-${range.from}-to-${range.to}.csv`);

    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

// helper fns
function indexChannels(map: Map<string, Channel>, channels: Channel[]): void {
  for (const channel of channels) {
    if (channel?.id && !map.has(channel.id)) {
      map.set(channel.id, channel);
    }
    if (channel?.alias && !map.has(channel.alias)) {
      map.set(channel.alias, channel);
    }
  }
}

async function loadChannelsForOUs(ouIds: string[] = []): Promise<Channel[]> {
  const uniqueOuIds = [...new Set(ouIds.filter(Boolean))];
  if (uniqueOuIds.length === 0) {
    return [];
  }

  const pages = await Promise.all(
    uniqueOuIds.map(async (ouId) => {
      const result = await FuelPortalService.getList(
        FuelEntityType.Channel,
        {
          ouId,
          page: 0,
          size: 500,
          sortBy: 'modifiedDate',
          orderBy: 'desc',
          showDeleted: false,
          enabled: false,
        },
        '&excludeSubtype=MediaLibrary'
      );
      return (result?.data ?? []) as Channel[];
    })
  );

  return pages.flat();
}

function csvCell(value: string | number | undefined | null): string {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

async function processBatch(
  clipIds: string[],
  map: Record<string, ClipAnalyticsRow>,
  range: { from: string; to: string }
) {
  if (clipIds.length === 0) {
    return '';
  }

  const clips = (await FuelPortalService.batchGet<Item[]>(FuelEntityType.Clip, clipIds)) || [];
  const clipById = new Map(clips.map((clip) => [clip.id, clip as any]));
  let update = '';

  // One CSV row per snowflake video id (match ContentAnalyticsList stubs for missing portal clips)
  for (const clipId of clipIds) {
    const match = map[clipId];
    if (!match) {
      continue;
    }

    const clip = clipById.get(clipId);
    if (clip) {
      const clipDuration = clip.data?.technicalMetadata?.meta?.duration;
      update += `${clip.title?.trim() ?? ''},`;
      update += `${clipId},`;
      update += `${clip.type || ''},`;
      update += `${clip.series || ''},`;
      update += `${clip.season || ''},`;
      update += `${clipDuration},`;
      update += `${match.views},`;
      update += `${match.watchTime},`;
      update += `${match.averageWatchTime},`;
      update += `${range.from},${range.to}\n`;
    } else {
      // Stub like the table: title from snowflake or id; empty Type/Series/Season/Duration
      const stubTitle = match.title ?? clipId;
      update += `${stubTitle},${clipId},,,,,`;
      update += `${match.views},`;
      update += `${match.watchTime},`;
      update += `${match.averageWatchTime},`;
      update += `${range.from},${range.to}\n`;
    }
  }

  return update;
}
